/**
 * AccountStatement Interface
 * Contains all Account Statement data collected during the onboarding process for all user roles
 * 
 * @interface AccountStatement
 * @author Financial Insights Platform
 * @version 1.0
 */
import { BenchmarkMonthlyReturn } from './BenchmarkMonthlyReturn';
export interface AccountStatement {
    id: string;
    accountId: string;
    statementDate: string;
    startDate: string;
    endDate: string;
    periodType: string;
    startingValue: number;
    endingValue: number;
    deposits: number;
    withdrawals: number;
    fees: number;
    dividendsEarned: number;
    interestEarned: number;
    netReturn: number;
    timeWeightedReturn: number;
    moneyWeightedReturn: number;

    /** MANUAL | CSV | PLAID */
    source?: string;
    externalStatementId?: string;
    documentUrl?: string;
    downloadedAt?: string;
}

export const sampleAccountStatements: AccountStatement[] = [];

function groupBenchmarks(benchmarks: BenchmarkMonthlyReturn[]): Record<string, { openingPrice: number; closingPrice: number }> {
  return benchmarks.reduce(
    (acc, b) => {
      const month = new Date(b.startDate).toLocaleString("default", { month: "short" });
      if (!acc[month]) {
        acc[month] = { openingPrice: b.openingPrice, closingPrice: b.closingPrice };
      } else {
        acc[month].closingPrice = b.closingPrice;
      }
      return acc;
    },
    {} as Record<string, { openingPrice: number; closingPrice: number }>
  );
}

function computeCumulative(
  grouped: Record<string, { openingPrice: number; closingPrice: number }>,
  sortedMonths: string[]
): number[] {
  let cum = 0;
  return sortedMonths.map((month) => {
    const bm = grouped[month];
    if (bm && bm.openingPrice > 0) {
      const monthlyReturn = ((bm.closingPrice - bm.openingPrice) / bm.openingPrice) * 100;
      cum = (1 + cum) * (1 + monthlyReturn / 100) - 1;
    }
    return Math.round(cum * 1000) / 10;
  });
}

export function computeCumulativePortfolioReturn(
  statements: AccountStatement[],
  sp500Benchmarks?: BenchmarkMonthlyReturn[],
  nasdaqBenchmarks?: BenchmarkMonthlyReturn[],
  dowBenchmarks?: BenchmarkMonthlyReturn[]
): { month: string; portfolio: number; benchmark: number; nasdaq: number; dow: number }[] {
  const grouped = statements.reduce(
    (acc, stmt) => {
      const month = new Date(stmt.statementDate).toLocaleString("default", { month: "short" });
      if (!acc[month]) {
        acc[month] = { startValue: 0, endValue: 0, deposits: 0, withdrawals: 0, fees: 0, dividends: 0, interest: 0 };
      }
      acc[month].startValue += stmt.startingValue;
      acc[month].endValue += stmt.endingValue;
      acc[month].deposits += stmt.deposits;
      acc[month].withdrawals += stmt.withdrawals;
      acc[month].fees += stmt.fees;
      acc[month].dividends += stmt.dividendsEarned;
      acc[month].interest += stmt.interestEarned;
      return acc;
    },
    {} as Record<string, { startValue: number; endValue: number; deposits: number; withdrawals: number; fees: number; dividends: number; interest: number }>
  );

  const monthOrder = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const sortedMonths = Object.keys(grouped).sort((a, b) => monthOrder.indexOf(a) - monthOrder.indexOf(b));

  const groupedSP500 = groupBenchmarks(sp500Benchmarks || []);
  const groupedNasdaq = groupBenchmarks(nasdaqBenchmarks || []);
  const groupedDow = groupBenchmarks(dowBenchmarks || []);

  const sp500Cumulative = computeCumulative(groupedSP500, sortedMonths);
  const nasdaqCumulative = computeCumulative(groupedNasdaq, sortedMonths);
  const dowCumulative = computeCumulative(groupedDow, sortedMonths);

  let cumulativeReturn = 0;
  const result: { month: string; portfolio: number; benchmark: number; nasdaq: number; dow: number }[] = [];

  for (let i = 0; i < sortedMonths.length; i++) {
    const month = sortedMonths[i];
    const g = grouped[month];
    const netDeposits = g.deposits - g.withdrawals - g.fees + g.dividends + g.interest;
    const monthlyReturn = g.startValue > 0
      ? ((g.endValue - netDeposits) / g.startValue - 1) * 100
      : 0;
    cumulativeReturn = (1 + cumulativeReturn) * (1 + monthlyReturn / 100) - 1;

    result.push({
      month,
      portfolio: Math.round(cumulativeReturn * 1000) / 10,
      benchmark: sp500Cumulative[i],
      nasdaq: nasdaqCumulative[i],
      dow: dowCumulative[i],
    });
  }

  return result;
}